/**
 * BigInt.prototype.toJSON polyfill for React 19 DEV mode compatibility.
 *
 * React 19's ReactPerformanceTrackProperties.js treats BigInt as primitive but
 * JSON.stringify doesn't support BigInt, causing crashes when logging prop diffs.
 * Havok physics uses BigInt for WASM memory pointers (HP_BodyId, HP_ShapeId, etc.).
 * This polyfill makes BigInt values serialize as strings (e.g., "332624").
 */
declare global {
  interface BigInt {
    toJSON(): string;
  }
}

if (typeof BigInt !== "undefined" && !BigInt.prototype.toJSON) {
  BigInt.prototype.toJSON = function () {
    return this.toString();
  };
}

import React from "react";
import ReactDOMClient from "react-dom/client";
import App from "@/App";
import { setupDevVariantHelpers } from "@/utils/devVariant";

// Import font files from @fontsource
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "@fontsource/ibm-plex-sans/700.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "@fontsource/ibm-plex-mono/600.css";

// Export specific components for users of the library
export { LoadingIcon } from "./components/Icons";

/**
 * Renders the application to the provided DOM container.
 * This function is exported for easier testing or potential multiple entry points.
 *
 * @param containerId - The ID of the DOM element where the app should be mounted.
 */
export function renderApp(containerId = "root"): void {
  const container = document.getElementById(containerId);

  if (!container) {
    throw new Error(`Could not find container with id '${containerId}'`);
  }

  const root = ReactDOMClient.createRoot(container);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

// Automatically render the app if this file is included at runtime,
// but skip it in test environments to allow more flexible testing setup.
if (import.meta.env.MODE !== "test") {
  setupDevVariantHelpers();
  renderApp();
}
